import { HierarchyContext } from "contexts/HierarchyContext";
import { useContext } from "react";
import { getHierarchyBaseUrl } from "../../contexts/hierarchy/getHierarchyBaseUrl";
import { eHierarchy, eHierarchyLevel } from "../../functions/api/enums";
import { ActiveNavLink } from "../common/ActiveNavLink";

/**
 * Navigation links for hierarchy types. Shows different navigation entries
 * depending on the hierarchy's type and level (Report, Directory, Project, etc.).
 */
export const TypeLinks = (): React.ReactElement => {
  const hierarchyContext = useContext(HierarchyContext);

  return !hierarchyContext ||
    (hierarchyContext.hierarchy?.type !== eHierarchy.Report &&
      hierarchyContext.hierarchy?.type !== eHierarchy.Directory &&
      hierarchyContext.hierarchy?.type !== eHierarchy.StudyDirectory) ? (
    <></>
  ) : (
    <>
      <h5
        className="mt-1"
        id="hierarchylinks-current-hierarchy"
      >
        {hierarchyContext.title}
      </h5>

      {hierarchyContext.hierarchy?.type === eHierarchy.Report ? (
        <>
          <div>
            <ActiveNavLink
              end
              to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}`}
            >
              Table of contents
            </ActiveNavLink>
          </div>
          <div>
            <ActiveNavLink
              end
              to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Review`}
            >
              Review
            </ActiveNavLink>
          </div>
          <>
            <div>
              <ActiveNavLink to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Properties`}>
                Properties
              </ActiveNavLink>
            </div>
            <div>
              <ActiveNavLink
                end
                to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Versions`}
              >
                Report Versions
              </ActiveNavLink>
            </div>
          </>
        </>
      ) : (
        <div>
          <ActiveNavLink
            id="hierarchylinks-properties"
            end
            to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}`}
          >
            Properties
          </ActiveNavLink>
        </div>
      )}
      <>
        <div>
          <ActiveNavLink
            id="hierarchylinks-access-rights"
            to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Access`}
          >
            Access rights
          </ActiveNavLink>
        </div>
        {hierarchyContext.hierarchy?.hasClinicalLibrary && (
          <div>
            <ActiveNavLink
              id="clinical-library"
              to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/ClinicalLibrary`}
            >
              Clinical library
            </ActiveNavLink>
          </div>
        )}
        {![eHierarchyLevel.Study, eHierarchy.Report].includes(hierarchyContext.hierarchy.level) && (
          <div>
            <ActiveNavLink
              id="hierarchylinks-domain-replacements"
              to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/DomainReplacements`}
            >
              Domain replacements
            </ActiveNavLink>
          </div>
        )}
        <div>
          <ActiveNavLink
            id="hierarchylinks-group-replacements"
            to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Lists`}
          >
            Group replacement lists
          </ActiveNavLink>
        </div>
        <div>
          <ActiveNavLink
            id="hierarchylinks-row-group-templates"
            to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/RowGroups`}
          >
            Row group templates
          </ActiveNavLink>
        </div>
        <div>
          <ActiveNavLink
            id="hierarchylinks-single-replacements"
            to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Replacements`}
          >
            Single replacements
          </ActiveNavLink>
        </div>
        {(hierarchyContext.hierarchy?.level === eHierarchyLevel.Project ||
          hierarchyContext.hierarchy?.level === eHierarchyLevel.Study) && (
          <div>
            <ActiveNavLink
              id="hierarchylinks-version-history"
              to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/Versions`}
            >
              Version History
            </ActiveNavLink>
          </div>
        )}
        {
          // Hidden For 5.0 Release
          /* {hierarchyContext.hierarchy && (
          <div>
            <ActiveNavLink
              id="hierarchylinks-advancedsearch"
              to={`${getHierarchyBaseUrl(hierarchyContext.hierarchy)}/AdvancedSearch`}
            >
              Advanced Search
            </ActiveNavLink>
          </div>
        )} */
        }
      </>
      <hr className="mt-3" />
    </>
  );
};

TypeLinks.displayName = "TypeLinks";
